import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FlexBetween from "components/FlexBetween";

const Register = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error("Passwords do not match");
      }

      if (!agreeTerms) {
        throw new Error("You must agree to the terms and conditions");
      }

      const response = await fetch(`${apiUrl}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      navigate("/login?registration=success");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        width="100%"
        maxWidth="500px"
        p="2rem"
        borderRadius="0.55rem"
        sx={{
          backgroundColor: theme.palette.background.alt,
          boxShadow: theme.shadows[4],
        }}
      >
        <FlexBetween flexDirection="column" gap="1.5rem">
          <Typography variant="h2" color={theme.palette.secondary[500]}>
            Create Account
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <Box display="flex" flexDirection="column" gap="1.5rem">
              <TextField
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  style: {
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              />

              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  style: {
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              />

              <TextField
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  style: {
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.background.default,
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  style: {
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  <Typography>
                    I agree to the{" "}
                    <span style={{ color: theme.palette.secondary[500] }}>
                      Terms of Service
                    </span>{" "}
                    and{" "}
                    <span style={{ color: theme.palette.secondary[500] }}>
                      Privacy Policy
                    </span>
                  </Typography>
                }
              />

              <Button
                type="submit"
                fullWidth
                disabled={isLoading}
                sx={{
                  p: "1rem",
                  backgroundColor: theme.palette.secondary[500],
                  color: theme.palette.background.alt,
                  "&:hover": {
                    backgroundColor: theme.palette.secondary[600],
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Register"
                )}
              </Button>
            </Box>
          </form>

          <Typography>
            Already have an account?{" "}
            <span
              style={{
                color: theme.palette.secondary[500],
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => navigate("/login")}
            >
              Login here
            </span>
          </Typography>
        </FlexBetween>
      </Box>
    </Box>
  );
};

export default Register;